import React, { useState } from 'react';
import {
  Paper,
  Box,
  Typography,
  IconButton,
  Collapse,
  Skeleton,
  Alert,
  Chip,
  Divider,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ExpandLessIcon from '@mui/icons-material/ExpandLess';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import RefreshIcon from '@mui/icons-material/Refresh';
import SettingsIcon from '@mui/icons-material/Settings';
import ReactMarkdown from 'react-markdown';
import { useTranslation } from 'react-i18next';
import useAISummary from './useAISummary';

function SummarySkeleton() {
  return (
    <Box sx={{ py: 0.5 }}>
      <Skeleton variant="text" width="100%" height={18} animation="wave" />
      <Skeleton variant="text" width="92%" height={18} animation="wave" />
      <Skeleton variant="text" width="85%" height={18} animation="wave" />
      <Skeleton variant="text" width="70%" height={18} animation="wave" />
    </Box>
  );
}

export default function AISummaryCard({
  patientId,
  examId,
  enabled = true,
  fillAvailable = false,
  onExpandedChange,
}) {
  const { t, i18n } = useTranslation();
  const [expanded, setExpanded] = useState(true);
  const [menuAnchor, setMenuAnchor] = useState(null);

  const handleToggleExpanded = () => {
    setExpanded((prev) => {
      const next = !prev;
      onExpandedChange?.(next);
      return next;
    });
  };

  const {
    summary,
    loading,
    regenerating,
    error,
    userPrompts,
    activePromptName,
    handleSwitchPrompt,
    handleRegenerate,
  } = useAISummary({ patientId, examId, enabled: enabled && !!patientId });

  const handlePromptSelect = (prompt) => {
    setMenuAnchor(null);
    if (prompt) {
      handleSwitchPrompt(prompt.name, prompt.prompt_text, prompt.scope, examId);
    } else {
      handleSwitchPrompt(null, null, null, examId);
    }
  };

  return (
    <Paper
      elevation={1}
      sx={{
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        minHeight: 0,
        ...(fillAvailable
          ? (expanded
            ? { flex: '1 1 0', minHeight: 0 }
            : { flex: '0 0 auto' })
          : { flexShrink: 0, maxHeight: expanded ? '30%' : 'auto' }),
      }}
    >
      <Box sx={{ px: 2, pt: 1.5, pb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
        <AutoAwesomeIcon sx={{ color: 'var(--rd-primary)', fontSize: 18 }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600, flex: 1, color: 'primary.main' }}>
          {t('aiSummary.title')}
        </Typography>
        <IconButton size="small" onClick={(e) => setMenuAnchor(e.currentTarget)} sx={{ color: 'var(--rd-primary)' }}>
          <ArrowDropDownIcon fontSize="small" />
        </IconButton>
        {summary?.status === 'COMPLETED' && (
          <IconButton size="small" onClick={() => handleRegenerate((i18n.language || 'de').substring(0, 2))} disabled={regenerating}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        )}
        <IconButton size="small" onClick={handleToggleExpanded}>
          {expanded ? <ExpandLessIcon fontSize="small" /> : <ExpandMoreIcon fontSize="small" />}
        </IconButton>
      </Box>

      <Menu anchorEl={menuAnchor} open={Boolean(menuAnchor)} onClose={() => setMenuAnchor(null)}>
        {userPrompts.map((prompt) => (
          <MenuItem key={prompt.id} onClick={() => handlePromptSelect(prompt)}>
            <ListItemIcon>
              <AutoAwesomeIcon fontSize="small" sx={{ color: prompt.is_default ? 'var(--rd-primary)' : 'text.secondary' }} />
            </ListItemIcon>
            <ListItemText primary={prompt.name} />
          </MenuItem>
        ))}
        {userPrompts.length > 0 && <Divider />}
        <MenuItem onClick={() => handlePromptSelect(null)}>
          <ListItemIcon><SettingsIcon fontSize="small" /></ListItemIcon>
          <ListItemText primary={t('aiSummary.systemDefault')} />
        </MenuItem>
      </Menu>

      <Collapse
        in={expanded}
        sx={{
          flex: fillAvailable && expanded ? '1 1 0' : undefined,
          minHeight: 0,
          overflow: 'hidden',
          display: fillAvailable ? 'flex' : undefined,
          flexDirection: fillAvailable ? 'column' : undefined,
          '& .MuiCollapse-wrapper': fillAvailable ? { height: '100%' } : undefined,
          '& .MuiCollapse-wrapperInner': fillAvailable ? {
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            minHeight: 0,
          } : undefined,
        }}
      >
        <Box sx={{
          px: 2,
          pb: 2,
          overflow: 'auto',
          ...(fillAvailable ? { flex: '1 1 0', minHeight: 0 } : { maxHeight: 220 }),
        }}>
          {(loading || regenerating) && <SummarySkeleton />}

          {error && !loading && (
            <Alert severity="error" sx={{ py: 0, fontSize: '0.75rem' }}>{error}</Alert>
          )}

          {summary?.status === 'ERROR' && !loading && !regenerating && (
            <Alert severity="error" sx={{ py: 0, fontSize: '0.75rem' }}>
              {summary.error_message || t('aiSummary.error')}
            </Alert>
          )}

          {summary?.status === 'COMPLETED' && !loading && !regenerating && summary.summary_text && (
            <Box sx={{ fontSize: '0.8rem', '& p': { m: 0, mb: 0.5 }, '& ul': { pl: 2, my: 0.5 } }}>
              {activePromptName && (
                <Chip size="small" label={activePromptName} sx={{ mb: 1, height: 18, fontSize: '0.65rem' }} />
              )}
              <ReactMarkdown>{summary.summary_text}</ReactMarkdown>
            </Box>
          )}

          {!loading && !summary && !error && !regenerating && (
            <Typography variant="body2" color="text.secondary">{t('aiSummary.noSummary')}</Typography>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
}
